import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Card, Spin, Alert, message } from 'antd';

// Interface for alarm data
interface AlarmMarker {
  id: string;
  lat: number;
  lng: number;
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  user: string;
  timestamp: string;
  status: 'active' | 'resolved';
}

interface GoogleMapProps {
  alarms: AlarmMarker[];
  center: { lat: number; lng: number };
  zoom: number;
  clusterLevel: number;
  onMarkerClick?: (alarm: AlarmMarker) => void;
  onAddressResolved?: (alarm: AlarmMarker, address: string) => void;
  onStreetViewOpen?: (alarm: AlarmMarker) => void;
  onSendMessage?: (alarm: AlarmMarker) => void;
}

// Map component with clustering
const Map: React.FC<GoogleMapProps> = ({ 
  alarms, 
  center, 
  zoom, 
  clusterLevel,
  onMarkerClick,
  onAddressResolved,
  onStreetViewOpen,
  onSendMessage
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);

  // Initialize map
  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
      setMap(newMap);
    }
  }, [ref, map, center, zoom]);

  // Create markers and clusterer
  useEffect(() => {
    if (!map) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    if (clusterer) {
      clusterer.clearMarkers();
    }

    // Create new markers
    const newMarkers = alarms.map(alarm => {
      const icon = getMarkerIcon(alarm.severity, alarm.status);
      
      const marker = new window.google.maps.Marker({
        position: { lat: alarm.lat, lng: alarm.lng },
        icon,
        title: `${alarm.user} - ${alarm.type}`,
      });

      // Add click listener with enhanced features
      marker.addListener('click', async () => {
        if (onMarkerClick) {
          onMarkerClick(alarm);
        }
        
        // Reverse geocoding for address resolution
        let addressText = 'Adres çözümleniyor...';
        try {
          const geocoder = new window.google.maps.Geocoder();
          const result = await geocoder.geocode({ 
            location: { lat: alarm.lat, lng: alarm.lng } 
          });
          
          if (result.results && result.results.length > 0) {
            addressText = result.results[0].formatted_address;
            if (onAddressResolved) {
              onAddressResolved(alarm, addressText);
            }
          }
        } catch (error) {
          addressText = 'Adres bulunamadı';
        }
        
        // Enhanced info window with Street View + Messaging
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 12px; min-width: 300px; max-width: 400px;">
              <div style="border-bottom: 2px solid ${getSeverityColor(alarm.severity)}; padding-bottom: 8px; margin-bottom: 12px;">
                <h3 style="margin: 0; color: ${getSeverityColor(alarm.severity)}; display: flex; align-items: center;">
                  🚨 ${alarm.severity.toUpperCase()} ALARM
                </h3>
              </div>
              
              <div style="margin-bottom: 12px;">
                <p style="margin: 6px 0;"><strong>👤 Kullanıcı:</strong> ${alarm.user}</p>
                <p style="margin: 6px 0;"><strong>🔧 Tür:</strong> ${alarm.type}</p>
                <p style="margin: 6px 0;"><strong>📊 Durum:</strong> ${alarm.status}</p>
                <p style="margin: 6px 0;"><strong>⏰ Zaman:</strong> ${new Date(alarm.timestamp).toLocaleString('tr-TR')}</p>
                <p style="margin: 6px 0;"><strong>📍 Adres:</strong> <span id="address-${alarm.id}">${addressText}</span></p>
                <p style="margin: 6px 0;"><strong>🗺️ Koordinat:</strong> ${alarm.lat.toFixed(6)}, ${alarm.lng.toFixed(6)}</p>
              </div>
              
              <div style="display: flex; gap: 8px; flex-wrap: wrap;">
                <button 
                  onclick="openStreetView('${alarm.id}', ${alarm.lat}, ${alarm.lng})" 
                  style="padding: 8px 12px; background: #1890ff; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
                >
                  🏠 Street View
                </button>
                <button 
                  onclick="openMessaging('${alarm.id}', '${alarm.user}')" 
                  style="padding: 8px 12px; background: #52c41a; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
                >
                  💬 Mesaj Gönder
                </button>
                <button 
                  onclick="copyCoordinates(${alarm.lat}, ${alarm.lng})" 
                  style="padding: 8px 12px; background: #faad14; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
                >
                  📋 Koordinat Kopyala
                </button>
                <button 
                  onclick="openDirections(${alarm.lat}, ${alarm.lng})" 
                  style="padding: 8px 12px; background: #722ed1; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;"
                >
                  🚗 Yol Tarifi
                </button>
              </div>
            </div>
          `
        });
        
        // Global functions for info window buttons
        (window as any).openStreetView = (alarmId: string, lat: number, lng: number) => {
          const streetViewService = new window.google.maps.StreetViewService();
          streetViewService.getPanorama({
            location: { lat, lng },
            radius: 50
          }, (data, status) => {
            if (status === 'OK' && data) {
              // Open Street View in new window/modal
              const streetViewUrl = `https://maps.google.com/maps?q=&layer=c&cbll=${lat},${lng}&cbp=12,0,0,0,0`;
              window.open(streetViewUrl, '_blank', 'width=800,height=600');
              
              if (onStreetViewOpen) {
                onStreetViewOpen(alarm);
              }
            } else {
              alert('Bu lokasyon için Street View bulunamadı.');
            }
          });
        };
        
        (window as any).openMessaging = (alarmId: string, userName: string) => {
          if (onSendMessage) {
            onSendMessage(alarm);
          }
        };
        
        (window as any).copyCoordinates = (lat: number, lng: number) => {
          navigator.clipboard.writeText(`${lat}, ${lng}`);
          alert('Koordinatlar kopyalandı!');
        };
        
        (window as any).openDirections = (lat: number, lng: number) => {
          const directionsUrl = `https://maps.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
          window.open(directionsUrl, '_blank');
        };
        
        infoWindow.open(map, marker);
      });

      return marker;
    });

    // Create new clusterer
    const newClusterer = new MarkerClusterer({
      map,
      markers: newMarkers,
      gridSize: clusterLevel,
      styles: [
        {
          textColor: 'white',
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
              <circle cx="20" cy="20" r="18" fill="#52c41a" stroke="white" stroke-width="2"/>
              <text x="20" y="25" text-anchor="middle" fill="white" font-size="12" font-weight="bold">CLUSTER_SIZE</text>
            </svg>
          `),
          height: 40,
          width: 40,
        },
        {
          textColor: 'white',
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50">
              <circle cx="25" cy="25" r="23" fill="#faad14" stroke="white" stroke-width="2"/>
              <text x="25" y="30" text-anchor="middle" fill="white" font-size="14" font-weight="bold">CLUSTER_SIZE</text>
            </svg>
          `),
          height: 50,
          width: 50,
        },
        {
          textColor: 'white',
          url: 'data:image/svg+xml;base64,' + btoa(`
            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60">
              <circle cx="30" cy="30" r="28" fill="#ff4d4f" stroke="white" stroke-width="2"/>
              <text x="30" y="35" text-anchor="middle" fill="white" font-size="16" font-weight="bold">CLUSTER_SIZE</text>
            </svg>
          `),
          height: 60,
          width: 60,
        }
      ],
    });

    setMarkers(newMarkers);
    setClusterer(newClusterer);

    message.success(`${alarms.length} alarm yüklendi - Cluster seviyesi: ${clusterLevel}`);

  }, [map, alarms, clusterLevel, onMarkerClick]);

  return <div ref={ref} style={{ width: '100%', height: '600px' }} />;
};

// Get marker icon based on severity and status
const getMarkerIcon = (severity: string, status: string) => {
  const color = getSeverityColor(severity);
  const opacity = status === 'resolved' ? 0.5 : 1;
  
  return {
    url: `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="${color}" opacity="${opacity}" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="12" r="4" fill="white" opacity="${opacity}"/>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(24, 24),
    anchor: new window.google.maps.Point(12, 12),
  };
};

// Get color for severity
const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'critical': return '#ff4d4f';
    case 'high': return '#fa8c16';
    case 'medium': return '#faad14';
    case 'low': return '#52c41a';
    default: return '#13c2c2';
  }
};

// Render function for Wrapper
const render = (status: Status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <Card style={{ height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" tip="Google Maps yükleniyor..." />
        </Card>
      );
    case Status.FAILURE:
      return (
        <Card style={{ height: '600px' }}>
          <Alert
            message="Google Maps Yükleme Hatası"
            description="Google Maps API yüklenirken bir hata oluştu. API anahtarını kontrol edin."
            type="error"
            showIcon
          />
        </Card>
      );
    default:
      return null;
  }
};

// Main GoogleMap component with Wrapper
const GoogleMap: React.FC<GoogleMapProps> = (props) => {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return (
      <Card style={{ height: '600px' }}>
        <Alert
          message="Google Maps API Key Eksik"
          description="VITE_GOOGLE_MAPS_API_KEY environment variable tanımlanmamış."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Wrapper 
      apiKey={apiKey} 
      render={render}
      libraries={['marker']}
    >
      <Map {...props} />
    </Wrapper>
  );
};

export default GoogleMap;
export type { AlarmMarker };
